import { useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";

/**
 * Hook to manage dark mode state.
 * - Syncs with backend (dark_mode field on user profile) for cross-device persistence
 * - Falls back to localStorage for instant UI (no flash on page load)
 * - Toggles the 'dark' class on <html> element (Tailwind's class-based dark mode)
 * Returns [isDark, toggleDark, saving]
 */
const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });
  const [saving, setSaving] = useState(false);

  // Apply dark class + persist to localStorage on every change
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", isDark);
  }, [isDark]);

  // Sync initial value from backend (overrides localStorage if different)
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    axiosInstance.get("/api/user/profile/").then((res) => {
      const backendValue = res.data.dark_mode ?? false;
      setIsDark(backendValue);
    }).catch(() => {});
  }, []);

  const toggleDark = async () => {
    const newValue = !isDark;
    setIsDark(newValue); // instant UI update
    setSaving(true);
    try {
      await axiosInstance.patch("/api/user/profile/", { dark_mode: newValue });
    } catch (err) {
      console.error("Failed to save dark mode preference:", err);
    } finally {
      setSaving(false);
    }
  };

  return [isDark, toggleDark, saving];
};

export default useDarkMode;
